import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { RadioButton } from "primereact/radiobutton";
import { Toast } from "primereact/toast";
import posService from "../../services/pos/pos.service";

interface SourceType {
  id: number;
  name: string;
}

interface POSOption {
  posId: number;
  posCode: string;
  posName: string;
}

interface BatchDetails {
  stockId: number;
  posId: number;
  posCode: string;
  productId: number;
  productName: string;
  batchId: number;
  batchCode: string;
  stock: number;
  availableStock: number;
  expiryDate: string;
  measurementType: string;
}

interface OutwardDialogProps {
  visible: boolean;
  productId: number | null;
  posId: number | null;
  productName: string;
  posName: string;
  onHide: () => void;
  onSuccess: () => void;
  minOrderQty?: number;
}

const OutwardDialog: React.FC<OutwardDialogProps> = ({
  visible,
  productId,
  posId,
  productName,
  posName,
  onHide,
  onSuccess,
  minOrderQty,
}) => {
  const toast = useRef<Toast>(null);
  const [sourceTypes, setSourceTypes] = useState<SourceType[]>([]);
  const [selectedSourceTypeId, setSelectedSourceTypeId] = useState<number>(1); // Default to POS
  const [posList, setPosList] = useState<any[]>([]);
  const [selectedPos, setSelectedPos] = useState<string | null>(null);
  const [batches, setBatches] = useState<BatchDetails[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<BatchDetails | null>(null);
  const [quantity, setQuantity] = useState<number | null>(null);
  const [note, setNote] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const allowDecimals = minOrderQty ? minOrderQty % 1 !== 0 : false;

  const formatDateOnly = (dateString: string): string => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const showMessage = (summary: string, detail: string, severity: any) => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const fetchSourceTypes = async () => {
    try {
      const response = await posService.GetOutwardSourceType();
      if (response && Array.isArray(response)) {
        setSourceTypes(response);
        // Set default to first type (POS)
        if (response.length > 0) {
          setSelectedSourceTypeId(response[0].id);
        }
      }
    } catch (error) {
      console.error("Error fetching source types:", error);
      showMessage("Error", "Failed to load source types", "error");
    }
  };

  const fetchRemainingPOS = async () => {
    if (!posId) return;

    try {
      const response = await posService.GetRemainingPOS(posId);
      if (response && Array.isArray(response)) {
        setPosList(response);
        debugger
      } else {
        setPosList([]);
      }
    } catch (error) {
      console.error("Error fetching POS list:", error);
      showMessage("Error", "Failed to load POS list", "error");
      setPosList([]);
    }
  };

  const fetchBatches = async () => {
    if (!productId || !posId) return;

    try {
      setLoading(true);
      const response = await posService.getBatchDetailsByProductId(productId, posId);

      if (response?.stocks && Array.isArray(response.stocks)) {
        const availableBatches = response.stocks.filter(
          (batch: BatchDetails) => batch.availableStock > 0
        );
        setBatches(availableBatches);
      } else if (Array.isArray(response)) {
        const availableBatches = response.filter(
          (batch: BatchDetails) => batch.availableStock > 0
        );
        setBatches(availableBatches);
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
      showMessage("Error", "Failed to load batches", "error");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchSourceTypes();
      fetchBatches();
      // Reset form
      setSelectedPos(null);
      setSelectedBatch(null);
      setQuantity(null);
      setNote("");
    }
  }, [visible, productId, posId]);

  useEffect(() => {
    if (selectedSourceTypeId === 1) { // POS type
      fetchRemainingPOS();
    }
  }, [selectedSourceTypeId, posId]);

  const handleSave = async () => {
    // Validation
    if (!selectedBatch) {
      showMessage("Validation Error", "Please select a batch", "warn");
      return;
    }

    if (!quantity || quantity <= 0) {
      showMessage("Validation Error", "Quantity must be greater than 0", "warn");
      return;
    }

    if (quantity > selectedBatch.availableStock) {
      showMessage(
        "Validation Error",
        `Quantity cannot exceed available stock (${selectedBatch.availableStock})`,
        "warn"
      );
      return;
    }

    if (selectedSourceTypeId === 1 && !selectedPos) {
      showMessage("Validation Error", "Please select a target POS", "warn");
      return;
    }

    if (selectedSourceTypeId === 2 && !note.trim()) {
      showMessage("Validation Error", "Note is mandatory when Others is selected", "warn");
      return;
    }

    try {
      setLoading(true);
      
      const payload = {
        fromPosId: posId,
        toPosId: selectedPos ? Number(selectedPos) : null,
        productId: productId,
        batchId: selectedBatch.batchId,
        quantityToTransfer: quantity!,
        outwardSourceTypeId: selectedSourceTypeId,
        documentDetails: "",
        note: note || "",
      };
debugger
      const response = await posService.outwardProductFromBatch(payload);

      if (response?.isSuccess) {
        showMessage("Success", response.message || "Stock outward successfully", "success");
        onSuccess();
        onHide();
      } else {
        showMessage("Error", response?.message || "Failed to outward stock", "error");
      }
    } catch (error) {
      console.error("Error in outward stock:", error);
      showMessage("Error", "An error occurred while processing outward", "error");
    } finally {
      setLoading(false);
    }
  };

  const batchOptionTemplate = (option: BatchDetails) => {
    return (
      <div className="py-2 px-1 text-sm">
        <div className="font-medium text-gray-900">{option.batchCode}</div>
        <div className="text-gray-600 text-xs mt-1">
          {option.availableStock} available • Exp: {formatDateOnly(option.expiryDate)}
        </div>
      </div>
    );
  };

  const selectedBatchTemplate = (option: BatchDetails | null) => {
    if (!option) return <span className="text-gray-500">Select batch</span>;
    return (
      <div className="text-sm">
        <span className="font-medium text-gray-900">{option.batchCode}</span>
        <span className="text-gray-600 ml-2">({option.availableStock} avail)</span>
      </div>
    );
  };

  const isPosTransfer = selectedSourceTypeId === 1;

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        visible={visible}
        onHide={onHide}
        header="Outward Stock"
        modal
        className="p-fluid"
        style={{ width: "40vw" }}
        breakpoints={{ "960px": "75vw", "641px": "95vw" }}
      >
        <div>
          {/* POS Name and Product Name */}
             <div className="field mb-3">
                  <label className="font-semibold text-sm block mb-1">Product & POS</label>
          
                  <div className="flex gap-2">
                      {/* POS (30%) */}
                      <div style={{ flex: "0 0 20%" }}>
                      <InputText
                          value={posName}
                          disabled
                          className="w-full"
                          style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
                      />
                      </div>
          
                      {/* Product Name (70%) */}
                      <div style={{ flex: "1 0 80%" }}>
                      <InputText
                          value={productName}
                          disabled
                          className="w-full"
                          style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
                      />
                      </div>
                  </div>
                  </div>

          {/* Source Type Selection */}
          <div className="field mb-3">
            <label className="font-semibold text-sm block mb-2">
              Destination Type <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              {sourceTypes.map((type) => (
                <div key={type.id} className="flex align-items-center">
                  <RadioButton
                    inputId={`sourceType${type.id}`}
                    name="sourceType"
                    value={type.id}
                    onChange={(e) => {
                      setSelectedSourceTypeId(e.value);
                      setSelectedPos(null); // Reset POS selection when changing type
                    }}
                    checked={selectedSourceTypeId === type.id}
                  />
                  <label htmlFor={`sourceType${type.id}`} className="ml-2 cursor-pointer">
                    {type.name}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* POS Selection - Show only if POS is selected */}
          {isPosTransfer && (
            <div className="field mb-3">
              <label htmlFor="targetPos" className="font-semibold text-sm block mb-1">
                Select Target POS <span className="text-red-500">*</span>
              </label>
              <Dropdown
                // id="targetPos"
                value={selectedPos}
                options={posList}
                onChange={(e) => setSelectedPos(e.value)}
                optionLabel="posCode"
                optionValue="posID" 
                placeholder="Select a POS"
                filter
                filterBy="posCode"
                className="w-full"
                disabled={posList.length === 0}
                emptyMessage="No POS available"
                style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
              />
            </div>
          )}

          {/* Batch Selection */}
          <div className="field mb-3">
            <label htmlFor="batch" className="font-semibold text-sm block mb-1">
              Select Batch <span className="text-red-500">*</span>
            </label>
            <Dropdown
              id="batch"
              value={selectedBatch}
              options={batches}
              onChange={(e) => setSelectedBatch(e.value)}
              optionLabel="batchCode"
              placeholder="Select a batch"
              filter
              filterBy="batchCode"
              itemTemplate={batchOptionTemplate}
              valueTemplate={selectedBatchTemplate}
              className="w-full"
              disabled={loading || batches.length === 0}
              emptyMessage="No batches available"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
            {selectedBatch && (
              <small className="text-gray-600 block mt-2 flex items-center gap-2">
                Expires: <span className="font-semibold text-gray-900">{formatDateOnly(selectedBatch.expiryDate)}</span>
              </small>
            )}
          </div>

          {/* Quantity */}
          <div className="field mb-3">
            <label htmlFor="quantity" className="font-semibold text-sm block mb-1">
              Quantity <span className="text-red-500">*</span>
            </label>
            <InputNumber
              id="quantity"
              value={quantity}
              onValueChange={(e) => setQuantity(e.value ?? null)}
              placeholder="Enter quantity"
              min={0}
              minFractionDigits={0} 
              maxFractionDigits={allowDecimals ? 3 : 0} 
              useGrouping={false}
              className="w-full"
              disabled={!selectedBatch}
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
            {selectedBatch && (
              <small className="text-gray-600 block mt-2 flex items-center gap-2">
                Maximum available: <span className="font-semibold text-gray-900">{selectedBatch.availableStock}</span>
              </small>
            )}
          </div>

          {/* Note */}
          <div className="field mb-3">
            <label htmlFor="note" className="font-semibold text-sm block mb-1">
              Note {!isPosTransfer && <span className="text-red-500">*</span>}
            </label>
            <InputText
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={!isPosTransfer ? "Enter note (required)" : "Enter note (optional)"}
              className="w-full"
              style={{ border: "1px solid #ced4da", borderRadius: "4px" }}
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-2 justify-content-end mt-4">
            <Button
              label="Cancel"
              onClick={onHide}
              severity="secondary"
              className="text-black border-round p-button-m"
              disabled={loading}
            />
            <Button
              label="Save"
              onClick={handleSave}
              loading={loading}
              // disabled={loading || !selectedBatch || quantity === 0 || quantity === null || (isPosTransfer && !selectedPos)}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default OutwardDialog;
